/* Native disclosures remain usable without JS; the mobile dialog supplies focus
   containment, Escape handling and an inert backdrop without theme modal coupling. */
if (!customElements.get('mp-mega-menu')) {
  customElements.define('mp-mega-menu', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.desktop = this.querySelector('.mp-menu__desktop');
      this.drawer = this.querySelector('dialog');
      this.trigger = document.querySelector(`[data-menu-open="${this.id}"]`);
      this.header = this.closest('.header--mega');
      this.rootScreen = this.querySelector('[data-menu-root]');
      this.backButton = this.querySelector('[data-menu-back]');
      this.drawerScreens = [...this.querySelectorAll('[data-menu-screen]')];
      if (!this.drawer || !this.desktop) return;
      this.trigger?.removeAttribute('hidden');
      this.categories = [...this.desktop.querySelectorAll('[data-category]')];
      const listen = (target, type, callback) => target?.addEventListener(type, callback, { signal });
      listen(this.trigger, 'click', () => this.openDrawer());
      listen(this.querySelector('[data-menu-close]'), 'click', () => this.drawer.close());
      listen(this.drawer, 'close', () => this.afterClose());
      listen(this.drawer, 'keydown', event => this.trapFocus(event));
      listen(this.drawer, 'click', event => {
        if (event.target === this.drawer) {
          const rect = this.drawer.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.drawer.close();
        }
        if (event.target.closest('a[href]')) this.drawer.close();
      });
      listen(this.backButton, 'click', () => this.showDrawerRoot(true));
      this.querySelectorAll('[data-menu-forward]').forEach(button => {
        listen(button, 'click', () => this.showDrawerScreen(button));
      });
      // Keep one L3 accordion open per menu, including browsers without details[name].
      this.querySelectorAll('.mp-menu__subcategory').forEach(disclosure => {
        listen(disclosure, 'toggle', () => {
          if (!disclosure.open) return;
          const scope = disclosure.closest('nav');
          scope.querySelectorAll('.mp-menu__subcategory[open]').forEach(other => {
            if (other !== disclosure) other.open = false;
          });
        });
      });
      this.header.dataset.menuLayout = 'desktop';
      this.layoutObserver = new ResizeObserver(() => this.updateLayout());
      this.layoutObserver.observe(this);
      this.desktop.querySelectorAll('.mp-menu__top').forEach(item => this.layoutObserver.observe(item));
      listen(document.fonts, 'loadingdone', () => this.updateLayout());
      document.fonts.ready.then(() => { if (this.isConnected) this.updateLayout(); });
      this.updateLayout();
      listen(document, 'pointerdown', event => {
        if (!this.contains(event.target)) this.closeCategories();
      });
      listen(this.desktop, 'focusout', event => {
        if (!this.desktop.contains(event.relatedTarget)) this.closeCategories();
      });
      listen(this.desktop, 'focusin', event => {
        this.closeCategories(event.target.closest('[data-category]'));
      });
      listen(window, 'resize', () => {
        this.updateLayout();
        this.categories.filter(category => category.open).forEach(category => this.sizePanel(category));
      });
      listen(this.desktop, 'keydown', event => {
        const category = event.target.closest('[data-category]');
        const disclosure = event.target.closest('details[open]');
        if (event.key === 'Escape' && disclosure) {
          event.preventDefault();
          event.stopPropagation();
          if (disclosure === category) this.closeCategories();
          else disclosure.open = false;
          disclosure.querySelector('summary').focus();
        }
        if (event.key === 'ArrowDown' && event.target.matches('summary')) {
          event.preventDefault();
          const target = event.target.closest('details');
          if (target === category) this.openCategory(category);
          else target.open = true;
          target.querySelector('a')?.focus();
        }
      });
      this.categories.forEach(category => {
        listen(category, 'toggle', () => {
          if (category.open) {
            this.closeCategories(category);
            this.sizePanel(category);
          }
        });
        listen(category, 'pointerenter', event => {
          clearTimeout(this.closeTimer);
          if (this.dataset.hover !== 'true' || event.pointerType !== 'mouse') return;
          this.openTimer = setTimeout(() => this.openCategory(category), 120);
        });
        listen(category, 'pointerleave', () => {
          clearTimeout(this.openTimer);
          if (this.dataset.hover !== 'true') return;
          this.closeTimer = setTimeout(() => {
            if (!category.contains(document.activeElement)) category.open = false;
          }, 180);
        });
      });
    }

    updateLayout() {
      const list = this.desktop.querySelector('.mp-menu__list');
      const style = getComputedStyle(list);
      const available = list.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const required = [...list.children].reduce((width, item) => width + item.getBoundingClientRect().width, 0);
      const compact = required > available + 0.5;
      if (this.compact === compact) return;
      this.compact = compact;
      this.header.dataset.menuLayout = compact ? 'drawer' : 'desktop';
      this.desktop.inert = compact;
      this.desktop.setAttribute('aria-hidden', String(compact));
      this.closeCategories();
      if (!compact && this.drawer.open) this.drawer.close();
    }

    showDrawerScreen(button) {
      const screen = this.drawerScreens.find(item => item.id === button.dataset.menuForward);
      if (!screen) return;
      this.drawerCategory = button;
      this.rootScreen.hidden = true;
      this.drawerScreens.forEach(item => { item.hidden = item !== screen; });
      button.setAttribute('aria-expanded', 'true');
      this.backButton.hidden = false;
      this.backButton.querySelector('[data-menu-title]').textContent = button.textContent.trim();
      this.drawer.querySelector('.mp-menu__drawer-content').scrollTop = 0;
      this.backButton.focus();
    }

    showDrawerRoot(restoreFocus = false) {
      this.rootScreen.hidden = false;
      this.drawerScreens.forEach(item => {
        item.hidden = true;
        item.querySelectorAll('details[open]').forEach(details => { details.open = false; });
      });
      this.backButton.hidden = true;
      this.drawerCategory?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.drawerCategory?.focus();
      this.drawerCategory = null;
      this.drawer.querySelector('.mp-menu__drawer-content').scrollTop = 0;
    }

    closeCategories(except) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.categories?.forEach(category => { if (category !== except) category.open = false; });
    }

    openCategory(category) {
      this.closeCategories(category);
      category.open = true;
      this.sizePanel(category);
    }

    sizePanel(category) {
      // Cap tall panels to the space actually remaining below the navigation.
      const panel = category.querySelector('.mp-menu__panel');
      const top = panel.getBoundingClientRect().top;
      panel.style.maxHeight = `${Math.max(160, window.innerHeight - top - 16)}px`;
    }

    openDrawer() {
      if (!this.compact || this.drawer.open) return;
      this.showDrawerRoot();
      this.drawer.showModal();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.trigger?.setAttribute('aria-expanded', 'true');
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex="0"]')].filter(element => {
        if (!element.getClientRects().length) return false;
        // Closed native details can still report client rectangles for descendants.
        for (let parent = element.parentElement; parent && parent !== this.drawer; parent = parent.parentElement) {
          if (parent.tagName === 'DETAILS' && !parent.open && parent.querySelector('summary') !== element) return false;
        }
        return true;
      });
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    afterClose() {
      const wasOpen = this.previousOverflow !== undefined;
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (wasOpen && this.compact && this.trigger?.isConnected) this.trigger.focus();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.layoutObserver?.disconnect();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.drawer?.open) this.drawer.close();
      this.afterClose();
    }
  });
}
